import { useState, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';

/**
 * Custom hook for WebSocket functionality
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoConnect - Whether to auto-connect on mount
 * @param {string[]} options.subscriptions - Initial subscriptions
 * @param {Function} options.onError - Error callback
 * @returns {Object} WebSocket state and methods
 */
export const useWebSocket = (options = {}) => {
  const {
    autoConnect = true,
    subscriptions = [],
    onError = null
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStats, setConnectionStats] = useState({});
  
  const listenersRef = useRef(new Map());
  const subscriptionsRef = useRef(new Set());

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      setConnectionError(null);
      await websocketService.connect();
    } catch (error) {
      setConnectionError(error.message);
      if (onError) onError(error);
    }
  }, [onError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  // Subscribe to order updates
  const subscribeToOrder = useCallback((orderId) => {
    websocketService.subscribeToOrder(orderId);
    subscriptionsRef.current.add(`order:${orderId}`);
  }, []);

  // Unsubscribe from order updates
  const unsubscribeFromOrder = useCallback((orderId) => {
    websocketService.unsubscribeFromOrder(orderId);
    subscriptionsRef.current.delete(`order:${orderId}`);
  }, []);

  // Subscribe to all orders
  const subscribeToAllOrders = useCallback(() => {
    websocketService.subscribeToAllOrders();
    subscriptionsRef.current.add('all_orders');
  }, []);

  // Subscribe to reviewer updates
  const subscribeToReviewerUpdates = useCallback(() => {
    websocketService.subscribeToReviewerUpdates();
    subscriptionsRef.current.add('reviewer_updates');
  }, []);

  // Add event listener
  const addEventListener = useCallback((event, callback) => {
    websocketService.on(event, callback);
    
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(callback);
  }, []);

  // Remove event listener
  const removeEventListener = useCallback((event, callback) => {
    websocketService.off(event, callback);
    
    if (listenersRef.current.has(event)) {
      listenersRef.current.get(event).delete(callback);
    }
  }, []);

  // Setup WebSocket event listeners
  useEffect(() => {
    const handleConnected = () => {
      setIsConnected(true);
      setConnectionError(null);
      setConnectionStats(websocketService.getConnectionStats());
    };

    const handleDisconnected = () => {
      setIsConnected(false);
      setConnectionStats(websocketService.getConnectionStats());
    };

    const handleError = (error) => {
      setConnectionError(error.message || 'WebSocket error');
      if (onError) onError(error);
    };

    const handleMessage = (data) => {
      setLastMessage({
        data,
        timestamp: Date.now()
      });
    };

    // Add internal listeners
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('error', handleError);
    websocketService.on('orderUpdated', handleMessage);
    websocketService.on('orderStatusChanged', handleMessage);
    websocketService.on('orderAssigned', handleMessage);
    websocketService.on('orderCommentAdded', handleMessage);
    websocketService.on('orderFlagAdded', handleMessage);
    websocketService.on('reviewerWorkloadUpdated', handleMessage);
    websocketService.on('notification', handleMessage);

    return () => {
      // Cleanup internal listeners
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('error', handleError);
      websocketService.off('orderUpdated', handleMessage);
      websocketService.off('orderStatusChanged', handleMessage);
      websocketService.off('orderAssigned', handleMessage);
      websocketService.off('orderCommentAdded', handleMessage);
      websocketService.off('orderFlagAdded', handleMessage);
      websocketService.off('reviewerWorkloadUpdated', handleMessage);
      websocketService.off('notification', handleMessage);
    };
  }, [onError]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      // Cleanup all listeners on unmount
      listenersRef.current.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          websocketService.off(event, callback);
        });
      });
    };
  }, [autoConnect, connect]);

  // Setup initial subscriptions
  useEffect(() => {
    if (isConnected && subscriptions.length > 0) {
      subscriptions.forEach(subscription => {
        if (subscription === 'all_orders') {
          subscribeToAllOrders();
        } else if (subscription === 'reviewer_updates') {
          subscribeToReviewerUpdates();
        } else if (subscription.startsWith('order:')) {
          const orderId = subscription.replace('order:', '');
          subscribeToOrder(orderId);
        }
      });
    }
  }, [isConnected, subscriptions, subscribeToAllOrders, subscribeToReviewerUpdates, subscribeToOrder]);

  return {
    // Connection state
    isConnected,
    connectionError,
    connectionStats,
    lastMessage,
    
    // Connection methods
    connect,
    disconnect,
    
    // Subscription methods
    subscribeToOrder,
    unsubscribeFromOrder,
    subscribeToAllOrders,
    subscribeToReviewerUpdates,
    
    // Event handling
    addEventListener,
    removeEventListener,
    
    // Utility
    getActiveSubscriptions: () => Array.from(subscriptionsRef.current)
  };
};

/**
 * Hook for listening to specific order updates
 * @param {string} orderId - Order ID to listen to
 * @param {Function} onUpdate - Update callback
 * @returns {Object} Order-specific WebSocket state
 */
export const useOrderWebSocket = (orderId, onUpdate) => {
  const [orderData, setOrderData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const {
    isConnected,
    connectionError,
    subscribeToOrder,
    unsubscribeFromOrder,
    addEventListener,
    removeEventListener
  } = useWebSocket({ autoConnect: true });

  // Handle order updates
  useEffect(() => {
    if (!orderId) return;

    const handleOrderUpdate = (data) => {
      if (data.order_id === orderId) {
        setOrderData(data.order);
        setLastUpdate(Date.now());
        if (onUpdate) onUpdate(data);
      }
    };

    const handleStatusChange = (data) => {
      if (data.order_id === orderId) {
        setLastUpdate(Date.now());
        if (onUpdate) onUpdate(data);
      }
    };

    const handleAssignment = (data) => {
      if (data.order_id === orderId) {
        setLastUpdate(Date.now());
        if (onUpdate) onUpdate(data);
      }
    };

    const handleComment = (data) => {
      if (data.order_id === orderId) {
        setLastUpdate(Date.now());
        if (onUpdate) onUpdate(data);
      }
    };

    const handleFlag = (data) => {
      if (data.order_id === orderId) {
        setLastUpdate(Date.now());
        if (onUpdate) onUpdate(data);
      }
    };

    // Add event listeners
    addEventListener('orderUpdated', handleOrderUpdate);
    addEventListener('orderStatusChanged', handleStatusChange);
    addEventListener('orderAssigned', handleAssignment);
    addEventListener('orderCommentAdded', handleComment);
    addEventListener('orderFlagAdded', handleFlag);

    // Subscribe to order updates
    if (isConnected) {
      subscribeToOrder(orderId);
    }

    return () => {
      // Remove event listeners
      removeEventListener('orderUpdated', handleOrderUpdate);
      removeEventListener('orderStatusChanged', handleStatusChange);
      removeEventListener('orderAssigned', handleAssignment);
      removeEventListener('orderCommentAdded', handleComment);
      removeEventListener('orderFlagAdded', handleFlag);
      
      // Unsubscribe from order updates
      unsubscribeFromOrder(orderId);
    };
  }, [orderId, isConnected, onUpdate, subscribeToOrder, unsubscribeFromOrder, addEventListener, removeEventListener]);

  return {
    isConnected,
    connectionError,
    orderData,
    lastUpdate
  };
};

/**
 * Hook for listening to reviewer workload updates
 * @param {Function} onUpdate - Update callback
 * @returns {Object} Reviewer WebSocket state
 */
export const useReviewerWebSocket = (onUpdate) => {
  const [reviewerData, setReviewerData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const {
    isConnected,
    connectionError,
    subscribeToReviewerUpdates,
    addEventListener,
    removeEventListener
  } = useWebSocket({ autoConnect: true });

  useEffect(() => {
    const handleReviewerUpdate = (data) => {
      setReviewerData(data);
      setLastUpdate(Date.now());
      if (onUpdate) onUpdate(data);
    };

    // Add event listener
    addEventListener('reviewerWorkloadUpdated', handleReviewerUpdate);

    // Subscribe to reviewer updates
    if (isConnected) {
      subscribeToReviewerUpdates();
    }

    return () => {
      removeEventListener('reviewerWorkloadUpdated', handleReviewerUpdate);
    };
  }, [isConnected, onUpdate, subscribeToReviewerUpdates, addEventListener, removeEventListener]);

  return {
    isConnected,
    connectionError,
    reviewerData,
    lastUpdate
  };
};

export default useWebSocket;